document.addEventListener('DOMContentLoaded', function() {

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.root = this;
      this.input = null;
      this.panel = null;
      this.status = null;
      this.controller = null;
      this.lastQuery = '';
      this.pointerIndex = -1;
      this.debounce = (fn, delay=150) => {
        let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
      };
    }

    connectedCallback() {
      const init = () => {
        this.input       = this.querySelector('.ps__input');
        this.panel       = this.querySelector('.ps__panel');
        this.status      = this.querySelector('[data-ps-status]');
        this.mobileClose = this.querySelector('.mobile-search-close');
    
        if (!this.input || !this.panel) return;
    
        if (this.mobileClose) {
          this.mobileClose.addEventListener('click', () => this.hidePanel());
        }
    
        this.input.addEventListener('input', this.debounce(() => this.onInput(), 160));
        this.root.addEventListener('keydown', (e) => this.onKeydown(e));
        document.addEventListener('click', (e) => {
          if (!this.root.contains(e.target)) this.hidePanel();
        });
        this.input.addEventListener('focus', () => {
          if (this.panel.innerHTML.trim()) this.showPanel();
        });
    
        const form = this.querySelector('.ps__form');
        if (form) {
          form.addEventListener('submit', (e) => {
            if (!this.input.value.trim()) {
              e.preventDefault();
              this.input.focus();
            }
          });
        }
      };
    
      // Try immediately
      init();
    
      // If HTML isn't ready, observe until it is
      if (!this.input || !this.panel) {
        const obs = new MutationObserver(() => {
          if (this.querySelector('.ps__input') &&
              this.querySelector('.ps__panel')) {
            obs.disconnect();
            init();
          }
        });
        obs.observe(this, { childList: true, subtree: true });
      }
    }
    

    hidePanel() {
      this.panel.innerHTML = '';
      this.input.value = '';
      this.panel.classList.add('ps__hidden');
      this.input.setAttribute('aria-expanded', 'false');
      this.setStatus('');
      this.pointerIndex = -1;
    }

    showPanel() {
      this.panel.classList.remove('ps__hidden');
      this.input.setAttribute('aria-expanded', 'true');
    }

    setStatus(msg) {
      if (this.status) this.status.textContent = msg;
    }

    productTemplate(item) {
      const img = item.image && (item.image.url || item.image);
      return `
        <a class="ps__row" role="option" href="${item.url}" data-ps-opt>
          ${img ? `<img class="ps__img" src="${img}" alt="" loading="lazy">` : `<div class="ps__img ps__img--ph"></div>`}
          <div class="ps__meta">
            <div class="ps__title">${item.title}</div>
          </div>
        </a>
      `;
    }

    simpleRow(label, url) {
      return `
        <a class="ps__row" role="option" href="${url}" data-ps-opt>
          <div class="ps__img ps__img--ph"></div>
          <div class="ps__meta">
            <div class="ps__title">${label}</div>
          </div>
        </a>
      `;
    }

    group(label, content) {
      return content ? `
        <div class="ps__group">
          <div class="ps__group-h">${label}</div>
          ${content}
        </div>
      ` : '';
    }

    render(data, q) {
      const res = (data && data.resources && data.resources.results) || {};
      const products   = res.products   || [];
      const collections= res.collections|| [];
      const pages      = res.pages      || [];
      const articles   = res.articles   || [];

      const filteredProducts = products.filter(p => !(p.tags && p.tags.includes('search_exclude')));
      const productsHTML = filteredProducts.map(this.productTemplate.bind(this)).join('');
      const collectionsHTML = collections.map(c => this.simpleRow(c.title, c.url)).join('');
      const pagesHTML = pages.map(p => this.simpleRow(p.title, p.url)).join('');
      const articlesHTML = articles.map(a => this.simpleRow(a.title, a.url)).join('');

      const html = `
        ${this.group('Products', productsHTML)}
        ${this.group('Collections', collectionsHTML)}
        ${this.group('Pages', pagesHTML)}
        ${this.group('Articles', articlesHTML)}
        <a class="ps__view-all" href="/search?q=${encodeURIComponent(q)}">View all results</a>
      `.trim();

      this.panel.innerHTML = html || `<div class="ps__empty">No results for “${this.escapeHtml(q)}”.</div>`;
      this.showPanel();

      const total = products.length + collections.length + pages.length + articles.length;
      this.setStatus(total ? `${total} results for ${q}` : `No results for ${q}`);
      this.pointerIndex = -1;
    }

    escapeHtml(s) {
      const d = document.createElement('div');
      d.textContent = s; return d.innerHTML;
    }

    async fetchSuggestions(q) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const url =
        `/search/suggest.json?q=${encodeURIComponent(q)}`
        + `&resources[type]=product,collection,article,page`
        + `&resources[fields]=title,vendor,product_type,variants.title`
        + `&limit=8`;

      const res = await fetch(url, {
        signal: this.controller.signal,
        headers: { 'Accept': 'application/json' }
      });

      if (!res.ok) throw new Error('Network error');
      return res.json();
    }

    async onInput() {
      const q = this.input.value.trim();
      if (!q) {
        this.hidePanel();
        return;
      }
      if (q === this.lastQuery) return;
      this.lastQuery = q;

      try {
        this.setStatus('Searching…');
        const json = await this.fetchSuggestions(q);
        this.render(json, q);
      } catch (e) {
        if (e.name === 'AbortError') return;
        this.setStatus('Something went wrong. Try again.');
        this.hidePanel();
      }
    }

    onKeydown(e) {
      const options = [...this.panel.querySelectorAll('[data-ps-opt]')];
      if (!options.length || this.panel.classList.contains('ps__hidden')) return;

      if (e.key === 'ArrowDown' || e.key === 'Down') {
        e.preventDefault();
        this.pointerIndex = (this.pointerIndex + 1) % options.length;
        options[this.pointerIndex].focus();
      } else if (e.key === 'ArrowUp' || e.key === 'Up') {
        e.preventDefault();
        this.pointerIndex = (this.pointerIndex - 1 + options.length) % options.length;
        options[this.pointerIndex].focus();
      } else if (e.key === 'Escape') {
        this.hidePanel();
        this.input.focus();
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);

});